var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, NumberInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../../common';
import { ExpandableRowLabel, TableHierarchyCell } from '../../common/tableHierarchy';
import { StyledCountryRow, StyledLocationTypeRow } from '../locationTable';
import { StyledSiteRow } from '../locationTable/styles';
import { useEffectiveGroupedLocations, useEffectiveLocationIndexMap, useEffectiveQueueLocations } from '../selectors';
import { useWizardSimpleContext } from '../useWizardSimpleContext';
var CENTERED_INPUT_PROPS = {
  style: {
    textAlign: 'center'
  }
};
var LocationSingleFieldTableHeader = function (_a) {
  var columnLabel = _a.columnLabel;
  return _jsx(TableHead, {
    children: _jsxs(TableRow, {
      children: [_jsx(TableCell, {
        sx: {
          width: '60%'
        }
      }), _jsx(TableCell, __assign({
        align: "center"
      }, {
        children: columnLabel
      }))]
    })
  });
};
var CountryGroup = function (_a) {
  var country = _a.country,
    countryLocations = _a.countryLocations,
    locIndexMap = _a.locIndexMap,
    props = __rest(_a, ["country", "countryLocations", "locIndexMap"]);
  var _b = useState(false),
    closed = _b[0],
    setClosed = _b[1];
  return _jsxs(Fragment, {
    children: [_jsxs(StyledCountryRow, {
      children: [_jsx(TableHierarchyCell, __assign({
        component: "th",
        align: "left",
        nestLevel: 2,
        sx: {
          py: 2
        }
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: !closed,
          onToggle: function () {
            return setClosed(function (p) {
              return !p;
            });
          },
          bold: true
        }, {
          children: country
        }))
      })), _jsx(TableCell, {})]
    }), !closed && countryLocations.map(function (location) {
      var locIdx = locIndexMap.get(location.id);
      if (locIdx === undefined) return null;
      return _jsxs(StyledSiteRow, {
        children: [_jsx(TableHierarchyCell, __assign({
          align: "left",
          nestLevel: 3
        }, {
          children: _jsx(Typography, {
            children: location.planLocation.locationName
          })
        })), _jsx(TableCell, __assign({
          align: "center",
          padding: "none"
        }, {
          children: _jsx(FormField, __assign({
            name: "queueLocations.".concat(locIdx, ".").concat(props.name),
            disableHelperText: true
          }, {
            children: _jsx(NumberInput, {
              disabled: props.disabled,
              decimal: props.decimal,
              min: props.min,
              max: props.max,
              placeholder: props.placeholder,
              suffix: props.suffix,
              fullWidth: true,
              inputProps: CENTERED_INPUT_PROPS
            })
          }))
        }))]
      }, location.id);
    })]
  });
};
var LocationTypeGroup = function (_a) {
  var locationType = _a.locationType,
    countries = _a.countries,
    locIndexMap = _a.locIndexMap,
    props = __rest(_a, ["locationType", "countries", "locIndexMap"]);
  var t = useTranslation('wizard').t;
  var _b = useState(false),
    closed = _b[0],
    setClosed = _b[1];
  var locationTypeKey = locationType;
  return _jsxs(Fragment, {
    children: [_jsxs(StyledLocationTypeRow, {
      children: [_jsx(TableHierarchyCell, __assign({
        component: "th",
        align: "left",
        nestLevel: 1,
        sx: {
          py: 2
        }
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: !closed,
          onToggle: function () {
            return setClosed(function (p) {
              return !p;
            });
          },
          bold: true
        }, {
          children: t("message.".concat(locationTypeKey))
        }))
      })), _jsx(TableCell, {})]
    }), !closed && Object.entries(countries).map(function (_a) {
      var country = _a[0],
        countryLocations = _a[1];
      return _jsx(CountryGroup, __assign({
        country: country,
        countryLocations: countryLocations,
        locIndexMap: locIndexMap
      }, props), country);
    })]
  });
};
var LocationSingleFieldTableBody = function (_a) {
  var _b;
  var name = _a.name,
    disabled = _a.disabled,
    min = _a.min,
    max = _a.max,
    decimal = _a.decimal,
    suffix = _a.suffix,
    placeholder = _a.placeholder;
  var locations = useEffectiveQueueLocations();
  var groupedLocations = useEffectiveGroupedLocations();
  var locIndexMap = useEffectiveLocationIndexMap();
  var queue = ((_b = useWizardSimpleContext()) !== null && _b !== void 0 ? _b : {}).queue;
  if (!locations.length) {
    return _jsx(TableBody, {
      children: _jsxs(TableRow, {
        children: [_jsx(TableCell, __assign({
          align: "left",
          component: "th"
        }, {
          children: _jsx(Typography, {
            children: queue === null || queue === void 0 ? void 0 : queue.name
          })
        })), _jsx(TableCell, __assign({
          align: "center",
          padding: "none"
        }, {
          children: _jsx(FormField, __assign({
            name: name,
            disableHelperText: true
          }, {
            children: _jsx(NumberInput, {
              disabled: disabled,
              decimal: decimal,
              min: min,
              max: max,
              placeholder: placeholder,
              suffix: suffix,
              fullWidth: true,
              inputProps: CENTERED_INPUT_PROPS
            })
          }))
        }))]
      })
    });
  }
  return _jsx(TableBody, {
    children: Object.entries(groupedLocations).map(function (_a) {
      var locationType = _a[0],
        countries = _a[1];
      return _jsx(LocationTypeGroup, {
        locationType: locationType,
        countries: countries,
        locIndexMap: locIndexMap,
        name: name,
        disabled: disabled,
        min: min,
        max: max,
        decimal: decimal,
        suffix: suffix,
        placeholder: placeholder
      }, locationType);
    })
  });
};
export var LocationSingleFieldTable = function (_a) {
  var columnLabel = _a.columnLabel,
    bodyProps = __rest(_a, ["columnLabel"]);
  return _jsx(TableContainer, {
    children: _jsxs(Table, __assign({
      sx: {
        tableLayout: 'fixed'
      }
    }, {
      children: [_jsx(LocationSingleFieldTableHeader, {
        columnLabel: columnLabel
      }), _jsx(LocationSingleFieldTableBody, __assign({}, bodyProps))]
    }))
  });
};